/**
 * Per-line GST split — single source of truth.
 *
 * CLAUDE.md rule: tax is **floored per line item** (never rounded at the
 * bill level), so the GSTR-1 export invariant `sum(line tax) === bill tax`
 * holds. This helper is reused by:
 *   - apps/pos/src/stores/use-cart-store.ts → computeLineItem (recompute on
 *     every cart mutation)
 *   - apps/pos/src/hooks/use-bill-calculations.ts → bill-discount pro-ration
 *     (re-floors per line after subtracting the pro-rated discount)
 *   - apps/pos/src/services/offline-billing.service.ts → offline submit
 *
 * Inputs:
 *   - taxable:    paise after the line discount (and any pro-rated bill
 *                 discount) has been subtracted
 *   - gstRate:    percentage as a number (e.g. 18 for 18 %)
 *   - isInterState: true → split goes entirely into IGST
 *                   false → split into CGST + SGST (SGST = floor half,
 *                   CGST takes the odd paisa — the server's rule)
 *
 * Returns the three split values in paise; the caller sums them as needed.
 */
import { roundToCashStep, type CashRoundingRule } from '@bbpos/utils';
import { getTaxRegime, type CurrencyCode } from '@bbpos/types';

// ─── GST registration (gst-parked 090, owner decision D5) ──────────────────
//
// A business that is not GST registered charges no GST: every line's tax is 0
// and the shelf price is what the customer pays. Held module-level, set by the
// auth store from /auth/me (`tenant.gst_registered`) on login and on an offline
// reload. Unknown (an older API) counts as registered, so nothing changes. The
// India GST regime only: a VAT tenant's `gst_registered` is always false, so
// the tenant currency is checked when the flag is set and VAT stays charged.

/** What the till, the popup and the slip print for an unregistered business. */
export const NOT_GST_REGISTERED = 'Not GST registered';

let waived = false;

export function setGstRegistration(
    registered: boolean | null | undefined,
    currency: string | null | undefined,
): void {
    // An unknown currency reads as INR, the app default (as setTenantCurrency does).
    const regime = getTaxRegime((currency || 'INR').toUpperCase() as CurrencyCode) ?? 'GST_INDIA';
    waived = registered === false && regime === 'GST_INDIA';
}

/** True when no GST is charged: India GST regime and the business is not registered. */
export function gstWaived(): boolean {
    return waived;
}

export interface LineGstSplit {
    cgst: number;
    sgst: number;
    igst: number;
}

/**
 * Regime-aware per-line tax (mirror of the server's `roundLineTax` in
 * services/api/src/billing/tax-presentation.ts, task 250):
 *  - `GST_INDIA` (INR) — `Math.floor` per line, split CGST/SGST or IGST below.
 *  - GCC VAT / `NO_VAT` — round-half-up to one whole currency minor unit
 *    (100 for 2-decimal, 1000 for the 3-decimal OMR/BHD/KWD), carried as a
 *    single VAT amount in the `igst` bucket exactly as the offline path does
 *    (`offline-billing.service.ts` → computeItemTotals). No CGST/SGST split.
 *
 * `currency` defaults to `'INR'` so every legacy caller stays byte-identical.
 */
export function computeLineGst(
    taxable: number,
    gstRate: number,
    isInterState: boolean,
    currency: CurrencyCode = 'INR',
): LineGstSplit {
    if (taxable <= 0 || gstRate <= 0 || gstWaived()) {
        return { cgst: 0, sgst: 0, igst: 0 };
    }
    if (getTaxRegime(currency) !== 'GST_INDIA') {
        // Half-up in minor units directly — a round trip through major units
        // turns an exact x.5 (101.5 fils) into 101.4999… and rounds it down.
        return { cgst: 0, sgst: 0, igst: Math.floor((taxable * gstRate) / 100 + 0.5) };
    }
    // Per-line tax floored once at the full GST rate, matching the server
    // (services/api/src/billing/billing.repository.ts → createBill /
    // applyDiscount, India branch of `roundLineTax`). Splitting the rate in
    // half and flooring twice (the prior code) dropped 1 paise per line
    // whenever the full-rate floor was odd, which made the UI's grand_total
    // 1 paise lower than the server's and caused `completeBill` to reject
    // payment with "Amount paid is less than total" (issue #94).
    const lineTax = Math.floor((taxable * gstRate) / 100);
    if (isInterState) {
        return { cgst: 0, sgst: 0, igst: lineTax };
    }
    // Intra-state split: CGST + SGST must sum to the full line tax so the
    // bill total stays in lock-step with the server. The server's rule
    // (bill-tax-supply.ts / tax-presentation.ts): SGST = floor(tax / 2) and
    // CGST takes the odd paisa (gst-parked 080).
    const sgst = Math.floor(lineTax / 2);
    return { cgst: lineTax - sgst, sgst, igst: 0 };
}

/**
 * Cash-round a bill total (minor units) with the till's rule (ops-simulation
 * T07: step + mode, see `@/lib/cash-rounding`). Same helper as the offline
 * path and the API; never a hardcoded `/ 100`.
 */
export function roundBillTotal(rawTotal: number, rule: CashRoundingRule): number {
    return roundToCashStep(rawTotal, rule);
}

/**
 * The cash round-off a tender may carry (ops-simulation T01): only cash is
 * rounded, so the full `roundOff` applies to a CASH payment or a split with a
 * cash leg, and every other tender is charged the exact total (0). The API
 * applies the same rule and rejects anything else; online and offline share it.
 */
export function cashRoundingForTender(
    method: string,
    splits: ReadonlyArray<{ method: string }> | undefined,
    roundOff: number,
): number {
    if (method === 'cash') return roundOff;
    if (method === 'split' && (splits ?? []).some((s) => s.method === 'cash')) return roundOff;
    return 0;
}
